import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, User, LogOut, Sun, Moon, Edit2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import AuthDialog from '@/components/AuthDialog';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_LOGO = 'https://customer-assets.emergentagent.com/job_5d93558c-03ee-4589-a69a-81ee86a30b68/artifacts/pn1o285k_ChatGPT%20Image%20Mar%202%2C%202026%2C%2003_22_50%20PM.png';

const defaultNavContent = {
  companyName: 'Challenge Packaging',
  tagline: 'INDUSTRIES',
  logoUrl: DEFAULT_LOGO,
};

export default function Navbar({ user, setUser, theme, toggleTheme, accentColor, onAccentChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [navContent, setNavContent] = useState(defaultNavContent);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(defaultNavContent);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/pages/navbar`);
        if (r.data?.content) setNavContent({ ...defaultNavContent, ...r.data.content });
      } catch (error) { console.error('Failed to load navbar content:', error); }
    })();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token'); localStorage.removeItem('user');
    setUser(null); toast.success('Logged out'); navigate('/');
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
      setEditData(prev => ({ ...prev, logoUrl: res.data.url.startsWith('http') ? res.data.url : `${BACKEND_URL}${res.data.url}` }));
      toast.success('Logo uploaded!');
    } catch (error) { console.error('Upload failed:', error); toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleSaveNav = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/pages/navbar`, { content: editData }, { headers: { Authorization: `Bearer ${token}` } });
      setNavContent(editData); toast.success('Navbar saved!'); setEditOpen(false);
    } catch (error) { console.error('Save failed:', error); toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/products' },
    { name: 'Brands', path: '/brands' },
    { name: 'Services', path: '/services' },
    { name: 'Sustainability', path: '/sustainability' },
    { name: 'Enquiry', path: '/enquiry' },
  ];

  return (
    <>
      <motion.nav className="professional-nav sticky top-0 z-50" initial={{ y: -100 }} animate={{ y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center gap-3">
              <motion.img src={navContent.logoUrl || DEFAULT_LOGO} alt="Logo" className="h-12 w-12 rounded-lg" whileHover={{ scale: 1.1, rotate: -5 }} transition={{ duration: 0.3 }} />
              <div className="hidden sm:block">
                <span className="text-base font-bold text-foreground tracking-tight leading-tight">{navContent.companyName}</span>
                <span className="text-[10px] block text-primary font-mono-spec tracking-widest -mt-0.5">{navContent.tagline}</span>
              </div>
              {user?.role === 'admin' && (
                <button onClick={(e) => { e.preventDefault(); setEditData(navContent); setEditOpen(true); }}
                  className="w-6 h-6 rounded-full bg-primary/80 hover:bg-primary text-primary-foreground flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity ml-1"
                  data-testid="edit-navbar-btn"><Edit2 className="w-3 h-3" /></button>
              )}
            </Link>

            <div className="hidden lg:flex items-center space-x-1">
              {navLinks.map((link) => (
                <Link key={link.path} to={link.path}
                  className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg ${location.pathname === link.path ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  {link.name}
                  {location.pathname === link.path && (
                    <motion.div className="absolute -bottom-1 left-3 right-3 h-0.5 rounded-full" style={{ background: 'linear-gradient(90deg, hsl(var(--primary)), hsl(var(--primary-end)))' }} layoutId="activeNav" transition={{ type: "spring", stiffness: 380, damping: 30 }} />
                  )}
                </Link>
              ))}
              <Link to="/contact">
                <motion.button className="ml-4 px-7 py-2.5 font-bold rounded-xl text-sm" style={{ background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-end)))' }} whileHover={{ scale: 1.05, boxShadow: '0 8px 30px hsl(var(--primary) / 0.3)' }} whileTap={{ scale: 0.95 }} data-testid="nav-link-contact">
                  <span className="text-primary-foreground">Contact</span>
                </motion.button>
              </Link>
            </div>

            <div className="hidden lg:flex items-center space-x-3">
              <motion.button onClick={toggleTheme} className="p-2.5 rounded-xl border border-border/30 bg-card/50 hover:border-primary/30 transition-all" whileHover={{ scale: 1.1, rotate: 15 }} whileTap={{ scale: 0.9 }} data-testid="theme-toggle-btn">
                {theme === 'dark' ? <Sun className="w-4 h-4 text-primary" /> : <Moon className="w-4 h-4 text-primary" />}
              </motion.button>
              {user?.role === 'admin' && onAccentChange && (
                <motion.button
                  onClick={() => onAccentChange(accentColor === 'green' ? 'yellow' : 'green')}
                  className="relative p-2 rounded-xl border border-border/30 bg-card/50 hover:border-primary/30 transition-all flex items-center gap-1.5"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  data-testid="accent-toggle-btn"
                  title={`Switch to ${accentColor === 'green' ? 'yellow' : 'green'} accent`}
                >
                  <span className={`w-4 h-4 rounded-full border-2 transition-all ${accentColor === 'green' ? 'border-white scale-110' : 'border-transparent opacity-50'}`} style={{ background: '#22c55e' }} />
                  <span className={`w-4 h-4 rounded-full border-2 transition-all ${accentColor === 'yellow' ? 'border-white scale-110' : 'border-transparent opacity-50'}`} style={{ background: '#eab308' }} />
                </motion.button>
              )}
              {user ? (
                <>
                  <Button onClick={() => navigate('/admin-dashboard')} variant="outline" className="border-border/30 hover:border-primary/30 rounded-xl" data-testid="dashboard-button"><User className="w-4 h-4 mr-2" /> Admin</Button>
                  <Button onClick={handleLogout} variant="ghost" className="text-muted-foreground hover:text-foreground rounded-xl" data-testid="logout-button"><LogOut className="w-4 h-4 mr-2" /> Logout</Button>
                </>
              ) : null}
            </div>

            <div className="flex lg:hidden items-center gap-2">
              <motion.button onClick={toggleTheme} className="p-2 rounded-xl border border-border/30 bg-card/50" whileTap={{ scale: 0.9 }} data-testid="theme-toggle-btn-mobile">
                {theme === 'dark' ? <Sun className="w-5 h-5 text-primary" /> : <Moon className="w-5 h-5 text-primary" />}
              </motion.button>
              <motion.button onClick={() => setIsOpen(!isOpen)} className="p-2 text-foreground hover:bg-muted rounded-xl" data-testid="mobile-menu-button" whileTap={{ scale: 0.9 }}>
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </motion.button>
            </div>
          </div>

          <AnimatePresence>
            {isOpen && (
              <motion.div className="lg:hidden py-4 border-t border-border/30" data-testid="mobile-menu" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                <div className="flex flex-col space-y-2">
                  {navLinks.map((link) => (
                    <Link key={link.path} to={link.path} onClick={() => setIsOpen(false)} className={`px-4 py-3 text-sm font-medium transition-colors rounded-xl ${location.pathname === link.path ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted'}`}>{link.name}</Link>
                  ))}
                  <Link to="/contact" onClick={() => setIsOpen(false)}><button className="w-full px-4 py-3 rounded-xl font-bold text-primary-foreground" style={{ background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-end)))' }}>Contact</button></Link>
                  <div className="pt-4 border-t border-border/30 space-y-2 px-4">
                    {user ? (
                      <>
                        <Button onClick={() => { navigate('/admin-dashboard'); setIsOpen(false); }} variant="outline" className="w-full rounded-xl">Admin Dashboard</Button>
                        <Button onClick={() => { handleLogout(); setIsOpen(false); }} variant="ghost" className="w-full rounded-xl">Logout</Button>
                      </>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.nav>

      <AuthDialog open={showAuthDialog} onOpenChange={setShowAuthDialog} mode={authMode} setMode={setAuthMode} setUser={setUser} />

      {/* Navbar Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md bg-card border-border/40">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Edit Navbar</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">Update company name, tagline, and logo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Company Name</Label>
              <Input value={editData.companyName} onChange={(e) => setEditData({...editData, companyName: e.target.value})} className="bg-background border-border/40 text-sm h-9" />
            </div>
            <div>
              <Label className="text-xs">Tagline</Label>
              <Input value={editData.tagline} onChange={(e) => setEditData({...editData, tagline: e.target.value})} className="bg-background border-border/40 text-sm h-9" />
            </div>
            <div>
              <Label className="text-xs">Logo</Label>
              <div className="flex items-center gap-3 mt-1">
                <img src={editData.logoUrl || DEFAULT_LOGO} alt="Logo" className="w-12 h-12 rounded-lg border border-border/40" />
                <div className="flex-1">
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" id="nav-logo-upload" disabled={uploading} />
                  <label htmlFor="nav-logo-upload" className="cursor-pointer text-xs text-primary hover:underline">{uploading ? 'Uploading...' : 'Upload new logo'}</label>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-border/30">
            <Button variant="outline" onClick={() => setEditOpen(false)} size="sm">Cancel</Button>
            <Button onClick={handleSaveNav} disabled={saving} size="sm" className="bg-primary"><Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving...' : 'Save'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
